import React, { useMemo } from 'react';
import './EvaluatedJobList.less';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import classNames from 'classnames';
import { JobType } from '../../../../redux/jobs/jobs.types';
import { applicationSelectors } from '../../../../redux/application';
import { isJobEvaluable } from '../../../../helper/EvaluationHelper';
import { searchSelectors } from '../../../../redux/search';
import JobRowList, { JobRow } from '../../../common/JobRowList';

interface IEvaluatedJobListProps {
    jobs: JobType[];
    className?: string;
}

const EvaluatedJobList = ({ jobs, className }: IEvaluatedJobListProps) => {
    const { t } = useTranslation();

    const { jobId } = useParams<{ jobId?: string }>();

    const isEvaluationShowOnlyDoneJobsAllowed = useSelector(applicationSelectors.isEvaluationShowOnlyDoneJobsAllowed);
    const searchValue = useSelector(searchSelectors.getSearchValue);

    // Single pass instead of three filters plus a useState/useEffect mirror —
    // the latter cost an extra render over the whole list on every change.
    const { jobsDoneWithEvaluation, jobsOpenWithEvaluation, jobsWithoutEvaluation } = useMemo(() => {
        const done: JobType[] = [];
        const open: JobType[] = [];
        const without: JobType[] = [];

        for (const job of jobs) {
            const evaluable = isJobEvaluable(job);
            if (job.done && evaluable) {
                done.push(job);
            } else if (!isEvaluationShowOnlyDoneJobsAllowed && !job.done && evaluable) {
                open.push(job);
            } else if (!isEvaluationShowOnlyDoneJobsAllowed && !evaluable) {
                without.push(job);
            }
        }

        return { jobsDoneWithEvaluation: done, jobsOpenWithEvaluation: open, jobsWithoutEvaluation: without };
    }, [jobs, isEvaluationShowOnlyDoneJobsAllowed]);

    const hasJobs = useMemo(
        () =>
            !(
                jobsOpenWithEvaluation.length === 0 &&
                jobsDoneWithEvaluation.length === 0 &&
                jobsWithoutEvaluation.length === 0 &&
                !searchValue
            ),
        [jobsOpenWithEvaluation, jobsDoneWithEvaluation, jobsWithoutEvaluation, searchValue],
    );

    // Order mirrors the previous markup: done-with-evaluation, then
    // open-with-evaluation, then the non-evaluable ones, each behind its own
    // separator that only appears when the section is non-empty.
    const rows = useMemo<JobRow[]>(() => {
        const result: JobRow[] = [];

        if (jobsDoneWithEvaluation.length > 0) {
            result.push({ kind: 'separator', key: 'separator-done', text: t('jobs.labels.readingsDone') });
            for (const job of jobsDoneWithEvaluation) {
                result.push({ kind: 'job', key: job.id, job, iconRight: 'ARROW' });
            }
        }

        if (!isEvaluationShowOnlyDoneJobsAllowed) {
            if (jobsOpenWithEvaluation.length > 0) {
                result.push({ kind: 'separator', key: 'separator-not-done', text: t('evaluation.labels.readingsNotDone') });
                for (const job of jobsOpenWithEvaluation) {
                    result.push({ kind: 'job', key: job.id, job, iconRight: 'ARROW', isGhost: !job.done });
                }
            }

            if (jobsWithoutEvaluation.length > 0) {
                result.push({
                    kind: 'separator',
                    key: 'separator-without-evaluation',
                    text: t('evaluation.labels.readingsWithoutEvaluation'),
                    isActive: false,
                });
                for (const job of jobsWithoutEvaluation) {
                    result.push({ kind: 'job', key: job.id, job, iconRight: 'ARROW', isGhost: true });
                }
            }
        }

        return result;
    }, [jobsDoneWithEvaluation, jobsOpenWithEvaluation, jobsWithoutEvaluation, isEvaluationShowOnlyDoneJobsAllowed, t]);

    return (
        <div className={classNames('evaluated-job-list flex flex-col flex-1 gap-2', className)}>
            {!hasJobs && (
                <div className={'bg-gray-200 p-4 rounded text-gray-700 text-sm text-center '}>{t('evaluation.empty.noJobs')}</div>
            )}

            <JobRowList rows={rows} routeBasePath={'/auswertung'} activeJobId={jobId} />

            {searchValue.length > 0 && jobs.length === 0 && (
                <div className={'bg-gray-200 p-4 rounded text-gray-700 text-sm text-center '}>
                    <Trans i18nKey={'jobs.search.noResults'} values={{ searchValue }} tOptions={{ interpolation: { escapeValue: true } }}>
                        Es konnten keine Ergebnisse für die Suche nach <br /> <b>"{searchValue}"</b> <br /> gefunden werden.
                    </Trans>
                    <br />
                </div>
            )}
        </div>
    );
};

export default EvaluatedJobList;
